import React, { useEffect, useState, useCallback } from 'react';
import { useWidgetQuery } from './useWidgetQuery';
import { apiService } from '../../../services/api';
import { kpiDisplayValue, kpiTiedLabel } from './renderers/kpiFormat';
import { rangeLabel } from './insightsRange';

// One cell of a Summary row, and the reason the family's promise is real rather
// than aspirational: a tile owns its own query, so one tile failing costs one tile.
//
// It has to be its own component. `useWidgetQuery` is per-instance state over a
// MODULE-GLOBAL gate of three concurrent agent calls, so a six-tile row calling the
// hook six times from one component would break the rules of hooks and monopolise
// the gate for every other card on the dashboard.
//
// Two kinds of tile, and the difference is not cosmetic:
//
//   prompt   — a question, answered by the ordinary widget query path as a `kpi`.
//              Reuses the single-value contract, dataset authorization and failure
//              states already built, and takes a slot in the concurrency gate.
//   platform — a count about the Console itself. No agent, so no slot: queueing a
//              cheap count behind three agent runs would make the fast half of a
//              row as slow as the slow half for no reason.
//
// A tile fetches its own platform metric even though the endpoint accepts a list and
// could answer a whole row in one call. That is a choice, not an oversight: the
// saving is a fraction of a second for six tiles, and the cost would be that the two
// kinds of tile stop working the same way — the row would fetch for the platform
// ones while the prompted ones still fetch for themselves. If a row ever grows past
// a handful of platform tiles, batching in SummaryRow is the change to make, and the
// endpoint's per-metric results already support it without weakening isolation.

// What the parent is told about this tile, whichever way it resolved. The shape
// matches what SummaryRowRenderer draws from storage, so a live tile and a stored
// one are the same thing and neither needs its own reader.
//
// **The backend's message is deliberately not carried.** SummaryRow PATCHes these
// objects into the widget's `data`, so anything here is durable and readable by anyone
// who can read the widget -- the whole organisation, for an org-shared one. The very
// text this file argues is unsafe to put on screen (internal field names, Python types,
// and on the agent-error path an upstream service's raw error verbatim) would have been
// more exposed in storage than a hover would ever have made it. Nothing reads it:
// both this component and SummaryRowRenderer key on `error_type` alone. If a failure
// needs explaining, the log is where the explanation belongs.
function settled(label, result, errorType, timeRange, resolvedRange) {
  return {
    label,
    result: result ?? null,
    error_type: errorType || null,
    computed_at: new Date().toISOString(),
    // PRD-1467: persist the tile's range so the stored strip (SummaryRowRenderer)
    // can show each tile's range beneath it, not just the live render.
    time_range: timeRange ?? null,
    // The dates that range resolved to. Kept beside the result rather than read from
    // it, because an answer from the agent carries them in its metadata, which the
    // result does not include -- the stored strip would fall back to the preset name.
    resolved_time_range: resolvedRange ?? null,
  };
}

// What a tile says when it has no number. Every phrase here is written BY US and
// chosen by the failure kind. The backend's own message is never shown: some of
// those are the agent's prose, but others name internal field names, Python types
// and, on the agent-error path, an upstream service's raw error verbatim. Nothing in
// the payload marks which is which, so the frontend cannot tell a sentence meant for
// a reader from one meant for us — and a tile has room for two words, not a triage.
//
// Two buckets, plus the specific cases. "Couldn't answer" means the question did not
// produce a usable number and the tile needs editing. "Unavailable" means something
// outside the question went wrong and it is worth trying again.
const FAILURE_TEXT = {
  permission_denied: 'No access',
  empty_result: 'No data',
  not_single_value: 'Not one value',
  truncated: 'Answer cut short',
  // A family whose flag is off is a closed door, not a failure. Saying "unavailable"
  // would read as something broken and send someone looking for a fault there is not.
  feature_disabled: 'Not enabled',
  // The question ran but its answer cannot be a tile's number.
  validation_failed: "Couldn't answer",
  type_mismatch: "Couldn't answer",
  agent_error: "Couldn't answer",
  // Something outside the question.
  unknown_metric: 'Unavailable',
  network_error: 'Unavailable',
};

function SummaryTile({ tile, index, onSettled, onRationale, refreshKey, widgetId }) {
  const isPlatform = tile?.kind === 'platform';
  const label = tile?.label || `Tile ${index + 1}`;

  // Both kinds keep one hook call each so the component's hook order never varies
  // with the tile's kind; the prompt path is the only one that uses the query.
  const { status, data, error, errorType, metadata, executeQuery } = useWidgetQuery();
  const [platform, setPlatform] = useState({ status: 'idle', value: null, errorType: null, message: null });

  const runPlatform = useCallback(async () => {
    setPlatform({ status: 'loading', value: null, errorType: null, message: null });
    try {
      const res = await apiService.getPlatformMetrics([tile.metric]);
      const entry = res?.metrics?.[tile.metric];
      if (entry && entry.error_type) {
        setPlatform({ status: 'error', value: null, errorType: entry.error_type, message: entry.message || null });
      } else if (entry && typeof entry.value === 'number') {
        setPlatform({ status: 'success', value: entry.value, errorType: null, message: null });
      } else {
        // A metric the server did not answer at all. Named rather than blank, so the
        // tile cannot read as a legitimate zero.
        setPlatform({ status: 'error', value: null, errorType: 'unknown_metric', message: 'This metric was not returned.' });
      }
    } catch (e) {
      const disabled = e?.status === 403;
      setPlatform({
        status: 'error',
        value: null,
        errorType: disabled ? 'feature_disabled' : 'error',
        message: disabled ? 'Metric widgets are not enabled.' : 'This metric could not be loaded.',
      });
    }
  }, [tile?.metric]);

  useEffect(() => {
    if (isPlatform) {
      if (tile?.metric) runPlatform();
      return;
    }
    if (tile?.prompt) {
      // widget_id + tile_index let the server re-run THIS tile's stored query
      // instead of regenerating it. Omitted for an unsaved preview, which has no
      // stored query to find, and the server falls back to the agent for it.
      executeQuery('kpi', tile.prompt, tile.dataset_ids || [], tile.time_range, {
        ...(widgetId ? { widget_id: widgetId, tile_index: index } : {}),
      });
    }
    // refreshKey is in the deps on purpose: the card's Refresh re-runs every tile by
    // changing it, without this component needing an imperative handle.
    //
    // dataset_ids is serialised rather than passed by reference: it is an array whose
    // identity churns on every render, so comparing it directly would re-query
    // constantly. Leaving it out entirely was worse — a tile repointed at a different
    // dataset kept answering from the old one, and that stale number was then saved.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatform, tile?.metric, tile?.prompt, tile?.time_range, JSON.stringify(tile?.dataset_ids || []), refreshKey, widgetId, index]);

  // Report upward once this tile has settled, so the row can persist the whole strip
  // in one write. Reporting per-render would have each tile's result racing its
  // siblings through the same PATCH.
  const state = isPlatform ? platform.status : status;
  useEffect(() => {
    if (state !== 'success' && state !== 'error') return;
    if (isPlatform) {
      // Platform tiles are a bare count with no date range.
      onSettled?.(index, settled(label, platform.value, platform.errorType, null));
    } else {
      const resolved = status === 'success' ? (data?.resolved_time_range || metadata?.resolved_time_range) : null;
      onSettled?.(index, settled(label, status === 'success' ? data : null, status === 'error' ? (errorType || 'error') : null, tile?.time_range, resolved));
      // The tile's reasoning, for the creator's preview. Reported on its own rather
      // than inside the settled result, which is saved as the strip's data: the saved
      // reasoning is the server's, read from each tile's stored query.
      onRationale?.(index, status === 'success' ? metadata?.rationale || null : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, platform.value, platform.errorType, data, metadata, errorType]);

  if (state === 'loading' || state === 'idle') {
    return (
      <div className="wc-sr-tile wc-sr-tile-loading">
        <span className="wc-sr-tile-label">{label}</span>
        {/* Three dots that rise in turn, so a strip mid-flight reads as working
            rather than stuck. A prompted tile is an agent run and can sit here for
            tens of seconds, which is exactly long enough for a static marker to look
            like a failure. The animation is CSS on three elements rather than a
            timer per tile: six tiles would otherwise mean six intervals re-rendering
            the row while it is already waiting on the network. */}
        <span className="wc-sr-tile-value wc-sr-tile-pending">
          <span className="wc-sr-dots" aria-hidden="true">
            <i /><i /><i />
          </span>
          <span className="wc-sr-only">{`${label}, loading`}</span>
        </span>
      </div>
    );
  }

  const failedType = isPlatform ? platform.errorType : (state === 'error' ? errorType || 'error' : null);
  if (failedType) {
    // No `title`: the backend's message does not reach the DOM at all, so there is
    // no hover, no devtools read and nothing to leak. A hover was the wrong home for
    // it anyway — invisible on touch and undiscoverable everywhere else.
    return (
      <div className="wc-sr-tile wc-sr-tile-failed">
        <span className="wc-sr-tile-label">{label}</span>
        <span className="wc-sr-tile-value">{FAILURE_TEXT[failedType] || 'Unavailable'}</span>
      </div>
    );
  }

  const shown = isPlatform
    ? kpiDisplayValue({ value_kind: 'number', value: platform.value })
    : kpiDisplayValue(data);
  const note = isPlatform ? '' : String(data?.value_label || '').trim();
  // A prompted tile is a kpi answer, so it can be a tie like any KPI card.
  const tied = isPlatform ? '' : kpiTiedLabel(data);
  return (
    <div className="wc-sr-tile">
      <span className="wc-sr-tile-label">{label}</span>
      <span className="wc-sr-tile-value" title={tied ? `${shown}, ${tied}` : shown}>{shown}</span>
      {tied && <span className="wc-sr-tile-tied">{tied}</span>}
      {note && <span className="wc-sr-tile-note">{note}</span>}
      {/* PRD-1467: each prompted tile shows its own range; platform counts have none. */}
      {!isPlatform && <span className="wc-sr-tile-range">{rangeLabel(tile?.time_range, data?.resolved_time_range || metadata?.resolved_time_range)}</span>}
    </div>
  );
}

export default SummaryTile;
